/**
 * The 2D plan canvas: the room's drawing, its handles, and its dim bars.
 *
 * Paint order is load-bearing (it doubles as hit-test priority): grid, floor
 * fills, the wall band (thickness), boundary segments, edge hit strips,
 * per-edge segments/alcoves, room corners, free elements, the add-bump icon,
 * then the read-only dim bars. Every handle carries a `data-handle`, the
 * protocol the pointer wiring in PlanStage dispatches on.
 */

import { EDGE_NAMES, FloorPlanRoom, Point } from '@kaiizen/kai-floor-plan';
import {
  addIconPoint,
  displayShape,
  EditorMutableState,
  FLOOR_PALETTE,
  floorSlabRings,
  leaves,
  modelToScreen,
  perpCursor,
  planGridPaths,
  primaryEdgeLineFrame,
  resolveScene,
  ringsPath,
  showsFloors,
  showsInterior,
  Viewport,
  wallPath,
} from '@kaiizen/kai-floor-plan-editor-core';
import { Fragment } from 'react';
import { counterFill, roomStroke } from '../../styles';
import { CornersLayer } from './CornersLayer';
import { DimBars } from './DimBars';
import { EdgeItems } from './EdgeItems';
import { FreeElements } from './FreeElements';
import { AddBumpIcon, EdgeSel, WallHitStrip } from './handles';
import { usePlanExport } from './planExportContext';
import { WallBand } from './WallBand';

/**
 * The drafting grid over the stage, as core draws it (`planGridPaths`): two
 * paths, the minor lines and then the major ones, bounded by the stage's size
 * at any zoom.
 */
export function PlanGrid({ vp, stageW, stageH }: { vp: Viewport; stageW: number; stageH: number }): React.JSX.Element {
  const { minorPath, majorPath, strokeWidth, minorColor, majorColor } = planGridPaths(vp, stageW || 800, stageH || 600);
  return (
    <>
      <path d={minorPath} fill='none' stroke={minorColor} strokeWidth={strokeWidth} />
      <path d={majorPath} fill='none' stroke={majorColor} strokeWidth={strokeWidth} />
    </>
  );
}

/** The `d` of model-space polygons (each an outer ring and its holes) on screen. */
const polygonsPath = (vp: Viewport, polygons: Point[][][]): string => ringsPath(polygons.flat().map(ring => ring.map(p => modelToScreen(vp, p))));

function floorColor(shape: FloorPlanRoom, type: string): string {
  if (type === shape.floorType) return FLOOR_PALETTE[0];
  const types = [...new Set((shape.freeElements ?? []).filter(r => r.customAreaType === 'flooring').map(r => r.floorType || shape.floorType))];
  const i = types.indexOf(type);
  return FLOOR_PALETTE[(i % (FLOOR_PALETTE.length - 1)) + 1];
}

export interface PlanSvgProps {
  st: EditorMutableState;
  stageW: number;
  stageH: number;
  /** Draw object names (default). Off in the assembler and the minimap. */
  showObjectLabels?: boolean;
}

export function PlanSvg({ st, stageW, stageH, showObjectLabels = true }: PlanSvgProps): React.JSX.Element {
  const exporting = usePlanExport();
  const vp = st.viewport;
  const s = displayShape(st);
  const scene = resolveScene(s);
  const sp = (p: Point) => modelToScreen(vp, p);
  const scale = vp.scale;
  // Object-name labels scale with the window: 11px at a 750px minor axis.
  const labelFontPx = 11 * (Math.min(stageW || 750, stageH || 750) / 750);
  const addIconPt = addIconPoint(st, s);
  const slab = floorSlabRings(s, st.layers);
  return (
    <>
      {!exporting && <PlanGrid vp={vp} stageW={stageW} stageH={stageH} />}
      {slab && (
        <g data-layer='floor'>
          <path d={polygonsPath(vp, [slab])} fill={FLOOR_PALETTE[0]} fillRule='evenodd' />
        </g>
      )}
      {showsFloors(st.layers)
        ? (
          <g data-layer='floor'>
            {scene.floors.map(f => {
              const path = polygonsPath(vp, f.polygons);
              return path ? <path key={f.type} d={path} fill={floorColor(s, f.type)} fillRule='evenodd' /> : null;
            })}
            {scene.counters.length > 0 && (
              <g data-layer='counter'>
                <path d={polygonsPath(vp, scene.counters)} fill={counterFill} fillRule='evenodd' opacity={0.75} />
              </g>
            )}
          </g>
        )
        : (
          <g data-layer='floor'>
            <path d={polygonsPath(vp, scene.roomSolid)} fill={FLOOR_PALETTE[0]} fillRule='evenodd' />
          </g>
        )}
      {
        /* Whole-room hit target: a bare-floor click selects the room. Painted
          before every interactive handle, so walls, segments, corners, and
          free elements all win the hit test over it. */
      }
      {!exporting && <path d={polygonsPath(vp, scene.roomSolid)} fill='transparent' fillRule='evenodd' data-handle='room' />}
      <WallBand s={s} sp={sp} />
      <g data-layer='wall'>
        {scene.segments.map((seg, i) => {
          const a = sp(seg.a), b = sp(seg.b);
          return (
            <line key={i} stroke={roomStroke} strokeWidth={1} x1={a.x} y1={a.y} x2={b.x} y2={b.y} strokeDasharray={seg.height === 0 ? '7 5' : undefined} />
          );
        })}
      </g>
      {!exporting && EDGE_NAMES.map(edge => {
        const cur = perpCursor(primaryEdgeLineFrame(s, edge).perp);
        // A whole-room selection lights up all four primary edges.
        const selected = st.selection.kind === 'room'
          || (st.selection.kind === 'edge' && st.selection.edge === edge);
        // One source for both: the bare-wall runs of the consolidated walk. Each
        // carries its along-edge span (gs/ge) for the hit-target's data-handle, and
        // is stroked accent when the wall is selected. Stops at alcove mouths.
        return (
          <Fragment key={edge}>
            {leaves(wallPath(s, edge, { selected: true })).map((leaf, li) => {
              if (leaf.ref.k !== 'wall') return null;
              const a = sp(leaf.line.start), b = sp(leaf.line.end);
              return (
                <Fragment key={li}>
                  {selected && <EdgeSel points={[a, b]} />}
                  <WallHitStrip a={a} b={b} perp={leaf.line.perp} scale={scale} handle={`edge:${edge}:${leaf.ref.gs}:${leaf.ref.ge}`} cursor={cur} />
                </Fragment>
              );
            })}
          </Fragment>
        );
      })}
      <EdgeItems st={st} s={s} sp={sp} scale={scale} />
      <CornersLayer st={st} s={s} sp={sp} scale={scale} />
      {showsInterior(st.layers) && <FreeElements st={st} s={s} sp={sp} labelFontPx={labelFontPx} showObjectLabels={showObjectLabels} />}
      {!exporting && addIconPt && <AddBumpIcon p={sp(addIconPt)} />}
      {!exporting && <DimBars st={st} stageW={stageW} stageH={stageH} />}
    </>
  );
}
